import { ProcessedSearchResults } from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import { AnalyticsComponentName } from '@app/pages/i/analytics';
import { Box, Text } from '@redbubble/design-system';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { AppProps } from '@redbubble/next/types';
import {
  type Item as AnalyticsEcommerceItem,
  AnalyticsItemsList,
} from '@redbubble/next-analytics/gtm';
import { usePageProps } from '@redbubble/next-provider';
import {
  RSProductClickedLocation,
  RSProductClickedPageType,
} from '@redbubble/next-provider/rudderstack';
import { SearchResultsPageProps } from 'pages/shop/types';
import { useMemo } from 'react';
import { CarouselSection } from '../../../../components/CarouselSection';
import {
  CarouselSectionContext,
  CarouselSectionContextValue,
} from '../../../../components/CarouselSection/carouselSectionContext';
import { SearchUUIDStorageKey } from '../../../../utils/searchUUIDStorage';
import {
  parseCarouselItemToAnalyticsEcommerceItem,
  trackGA4ViewItemListEvent,
} from '../../../i/analytics';
import styles from './SuggestedProductsCarousel.module.scss';
import {
  mapSearchResultMediaCardProps,
  mapSearchResultPriceProps,
} from './utils';

const messages = defineMessages({
  heading: {
    defaultMessage: 'Most popular products',
  },
});

export interface SuggestedProductsCarouselProps {
  suggestedProducts: ProcessedSearchResults;
}

export const SuggestedProductsCarousel = ({
  suggestedProducts,
}: SuggestedProductsCarouselProps) => {
  const intl = useIntl();
  const { searchUUID, listsInclusions } = usePageProps<
    AppProps & SearchResultsPageProps
  >();

  const contextValue: CarouselSectionContextValue = useMemo(() => {
    return {
      mediaCardPropsMapper: mapSearchResultMediaCardProps(listsInclusions),
      pricePropsMapper: mapSearchResultPriceProps,
      analytics: {
        componentName: AnalyticsComponentName.SuggestedProducts,
        listID: AnalyticsItemsList.SuggestedProductsCarousel,
        searchUUIDStorageKey: SearchUUIDStorageKey.Latest,
        inViewTracking: () => {
          const analyticsItems = suggestedProducts
            .map((item, index) => {
              return parseCarouselItemToAnalyticsEcommerceItem({
                item,
                index,
              });
            })
            .filter(
              (
                item: AnalyticsEcommerceItem | null
              ): item is NonNullable<AnalyticsEcommerceItem> => !!item
            );

          trackGA4ViewItemListEvent({
            items: analyticsItems,
            listID: AnalyticsItemsList.SuggestedProductsCarousel,
            topic: '',
            searchUUIDStorageKey: SearchUUIDStorageKey.Latest,
          });
        },
      },
      rsLocation: RSProductClickedLocation.SuggestedProductsCarousel,
      rsPageType: RSProductClickedPageType.Search,
    };
  }, [listsInclusions, suggestedProducts]);

  if (!suggestedProducts.length) return null;

  return (
    <Box className={styles.container} data-testid="suggested-products-section">
      <CarouselSectionContext.Provider value={contextValue}>
        <CarouselSection.Container>
          <Text type="display5">{intl.formatMessage(messages.heading)}</Text>
          <CarouselSection.Carousel>
            {suggestedProducts.map((product, index) => {
              return (
                <CarouselSection.CarouselSlide
                  key={`suggested-product-slide-${product.inventoryItem?.id}`}
                >
                  <CarouselSection.CarouselMediaCard
                    data={product}
                    index={index}
                    searchUUID={searchUUID}
                  />
                </CarouselSection.CarouselSlide>
              );
            })}
          </CarouselSection.Carousel>
        </CarouselSection.Container>
      </CarouselSectionContext.Provider>
    </Box>
  );
};
